import { useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  EndToEndSeparator,
  FormGroupDefinition,
  FormNode,
  formTraverse,
  GroupStyle,
  Separator,
} from '@msdk/definitions'
import {
  Box,
  CardBody,
  CardHeader,
  COLORS,
  createComponentWrapperWithRef,
  Divider,
  IconSelector,
  styled,
  Tooltip,
  Typography,
  uiTokens,
} from '@msdk/ui'
import React, { ReactNode } from 'react'

import { nodeHasFlag } from '../../../utils'
import { getWidthOfElement } from './utils'

const StyledMuiBox = styled(Box)`
  .MuiCardContent-root {
    padding-bottom: 0;
  }
  .ConditionalField-renderer {
    padding-bottom: 16px;
  }
`
const StyledNovaBox = styled(Box)`
  .MuiCardContent-root {
    padding-bottom: 0;
  }
  .ConditionalField-renderer {
    padding-bottom: ${uiTokens.spacing.padding.large}px;
  }
`
const StyledBox = createComponentWrapperWithRef(
  {
    mui: StyledMuiBox,
    nova: StyledNovaBox,
  },
  'StyledBox',
)

const MuiStyledCardBody = styled(CardBody)`
  display: flex;
  flex-flow: wrap;
  flex-direction: row;
  padding: 0;
  width: 100%;
  .MuiCardContent-root {
    padding-bottom: 0;
  }
`

const NovaStyledCardBody = styled(CardBody)`
  display: flex;
  flex-flow: wrap;
  flex-direction: row;
  padding: 0;
  width: 100%;
  .MuiCardContent-root {
    padding-bottom: 0;
  }
  &:last-child {
    padding-bottom: 0;
  }
`

const StyledCardBody = createComponentWrapperWithRef(
  {
    mui: MuiStyledCardBody,
    nova: NovaStyledCardBody,
  },
  'StyledCardBody',
)

const StyledSeparator = styled(Divider)`
  margin-bottom: 24px;
`
const StyledEndToEndSeparator = styled(Divider)`
  margin-bottom: 24px;
  margin-left: -24px;
  margin-right: -24px;
`

const HeaderMuiWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  margin-bottom: 12px;
`

const HeaderNovaWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  margin-bottom: 24px;
`
const HeaderWrapper = createComponentWrapperWithRef(
  {
    mui: HeaderMuiWrapper,
    nova: HeaderNovaWrapper,
  },
  'HeaderWrapper',
)

const TitleWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`

const IconContainer = styled('div')`
  height: 24px;
  width: 24px;
`

const StyledMuiCardHeader = styled(CardHeader, { shouldForwardProp: (prop) => prop !== '$groupStyle' })<{
  $groupStyle: GroupStyle
}>`
  ${(props) => props.$groupStyle === GroupStyle.SubGroup && `color: ${COLORS.text.secondary};`}
  padding: 0;

  .MuiCardHeader-title {
    color: inherit;
  }
`
const StyledNovaCardHeader = styled(CardHeader, { shouldForwardProp: (prop) => prop !== '$groupStyle' })<{
  $groupStyle: GroupStyle
}>`
  ${(props) => props.$groupStyle === GroupStyle.SubGroup && `color: ${COLORS.text.secondary};`}
  padding: 0;

  .MuiCardHeader-title {
    color: ${uiTokens.uiContent.neutral};
    padding-top: 0;
    padding-bottom: 0;
    ${(props) =>
      props.$groupStyle === GroupStyle.SubGroup &&
      `font-size: ${uiTokens.font.fontSize.body.medium}px; font-weight: ${uiTokens.font.fontWeight.regular}`}
  }
`
const StyledCardHeader = createComponentWrapperWithRef(
  {
    mui: StyledMuiCardHeader,
    nova: StyledNovaCardHeader,
  },
  'StyledCardHeader',
)

const StyledMuiWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== '$groupStyle' })<{
  $groupStyle: GroupStyle
}>`
  padding-bottom: 0px;
  ${(props) =>
    props.$groupStyle === GroupStyle.CenterGroup &&
    `
    padding: 16px 24px;
    border: 1px solid ${COLORS.border};
    border-radius: 8px;
    background-color: ${COLORS.components.form.group.background};
  `}
`

const StyledNovaWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== '$groupStyle' })<{
  $groupStyle: GroupStyle
}>`
  padding-bottom: 8px;
  ${(props) =>
    props.$groupStyle === GroupStyle.CenterGroup &&
    `
    padding: ${uiTokens.spacing.padding.medium}px;
    border: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
    border-radius: ${uiTokens.radius.large}px;
    background-color: ${uiTokens.uiSurface['wm-static']};
    align-items: flex-start;
    gap: ${uiTokens.spacing.gap.large}px;
  `}
`
const StyledWrapper = createComponentWrapperWithRef(
  {
    mui: StyledMuiWrapper,
    nova: StyledNovaWrapper,
  },
  'StyledWrapper',
)

const Mandatory = styled('span')`
  color: ${COLORS.error.main};
  margin-left: 5px;
`

interface Props {
  node: FormNode
  children?: ReactNode[]
}

export function FrameworkGroup({ node, children }: Props) {
  const groupDef = node.value.formDefinition as FormGroupDefinition
  const t = useTranslation()

  const caclWidh = (index: number) => {
    const item: any = node.id === 'ROOT' ? groupDef : groupDef.layout[index]
    return getWidthOfElement(item, groupDef, node)
  }

  const hasMandatory = () => {
    if (groupDef.showMandatory) {
      let result = false
      formTraverse(node, (item) => {
        const hasMandatoryFlag = nodeHasFlag(item, ControlValidationType.Mandatory)
        if (hasMandatoryFlag) result = true
      })
      return result
    }
    return false
  }

  return (
    <>
      {node.value.visibility === 'Visible' && (
        <>
          {[Separator.Top, Separator.Both].includes(groupDef.separator) && <StyledSeparator />}
          {[EndToEndSeparator.Top, EndToEndSeparator.Both].includes(groupDef.endToEndSeparator) && (
            <StyledEndToEndSeparator />
          )}
          <StyledWrapper $groupStyle={groupDef.groupStyle}>
            {groupDef.idml && !groupDef.hideGroupHeader && (
              <HeaderWrapper>
                <TitleWrapper>
                  <StyledCardHeader
                    title={
                      <span>
                        {t(groupDef.idml, groupDef.description)}
                        {hasMandatory() && <Mandatory>*</Mandatory>}
                      </span>
                    }
                    $groupStyle={groupDef.groupStyle}
                    className={groupDef.groupStyle == 30 ? 'centered-group' : ''}
                    titleTypographyProps={{
                      fontSize: groupDef.groupStyle === GroupStyle.SubGroup ? '16px' : '18px',
                      fontWeight: groupDef.groupStyle === GroupStyle.SubGroup ? '400' : '700',
                      padding: groupDef.groupStyle === GroupStyle.SubGroup ? '8px 0 8px 0' : '16px 0 8px 0',
                    }}
                  />
                  {groupDef.tooltip && (
                    <Tooltip title={t(groupDef.tooltip.idml, groupDef.tooltip.description)} placement="right" arrow>
                      <IconContainer>
                        <IconSelector iconName="InfoOutlined" color="primary" />
                      </IconContainer>
                    </Tooltip>
                  )}
                </TitleWrapper>
                {groupDef.subtitle && groupDef.subtitle.idml && groupDef.subtitle.description && (
                  <Typography variant="body2" color={COLORS.text.secondary}>
                    {t(groupDef.subtitle.idml, groupDef.subtitle.description)}
                  </Typography>
                )}
              </HeaderWrapper>
            )}

            <StyledCardBody>
              {children?.map((element, index) => (
                <StyledBox key={index} width={caclWidh(index)}>
                  {element}
                </StyledBox>
              ))}
            </StyledCardBody>
          </StyledWrapper>
          {[Separator.Bottom, Separator.Both].includes(groupDef.separator) && <StyledSeparator />}
          {[EndToEndSeparator.Bottom, EndToEndSeparator.Both].includes(groupDef.endToEndSeparator) && (
            <StyledEndToEndSeparator />
          )}
        </>
      )}
    </>
  )
}
